import { isLambdaStage } from "./layout";

export function buildTimeline(metrics, pipeline) {
  if (!pipeline?.stages?.length) return [];

  const timestamps = new Set();

  for (const stage of pipeline.stages) {
    if (isLambdaStage(stage)) {
      const points = metrics?.lambdas?.[stage.functionName]?.points ?? [];
      for (const point of points) {
        timestamps.add(point.timestamp);
      }
      continue;
    }

    const queueNames = [stage.queue, stage.dlq].filter(Boolean);
    for (const queueName of queueNames) {
      const points = metrics?.queues?.[queueName]?.points ?? [];
      for (const point of points) {
        timestamps.add(point.timestamp);
      }
    }
  }

  return Array.from(timestamps).sort();
}

function getPointAtExactTime(points, timestamp) {
  if (!timestamp || !points?.length) return null;
  return points.find((p) => p.timestamp === timestamp) ?? null;
}

function getQueuePointAtTime(metrics, queueName, timestamp) {
  if (!queueName) return null;
  const points = metrics.queues[queueName]?.points ?? [];
  return getPointAtExactTime(points, timestamp);
}

function getLambdaPointAtTime(metrics, functionName, timestamp) {
  if (!functionName) return null;
  const points = metrics.lambdas?.[functionName]?.points ?? [];
  return getPointAtExactTime(points, timestamp);
}

export function getQueueValuesAtTime(metrics, pipeline, timestamp) {
  if (!timestamp || !pipeline?.stages) return {};

  const values = {};
  for (const stage of pipeline.stages) {
    if (isLambdaStage(stage)) {
      const point = getLambdaPointAtTime(metrics, stage.functionName, timestamp);
      values[stage.key] = {
        invocations: point?.invocations ?? 0,
      };
      continue;
    }

    const point = getQueuePointAtTime(metrics, stage.queue, timestamp);
    const dlqPoint = getQueuePointAtTime(metrics, stage.dlq, timestamp);

    values[stage.key] = {
      sent: point?.sent ?? 0,
      deleted: point?.deleted ?? 0,
      dlqVisible: dlqPoint?.visible ?? 0,
    };
  }

  return values;
}

export function getGlobalPeakSent(metrics, pipeline) {
  if (!pipeline?.stages) return 0;

  return pipeline.stages.reduce((max, stage) => {
    if (isLambdaStage(stage)) {
      const lambdaMetrics = metrics?.lambdas?.[stage.functionName];
      return Math.max(max, lambdaMetrics?.peakInvocations ?? 0);
    }

    const queueMetrics = metrics?.queues?.[stage.queue];
    return Math.max(max, queueMetrics?.peakSent ?? 0);
  }, 0);
}
